import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./Popover";

/**
 * Reusable Combobox component using shadcn/ui Popover and Command.
 * Replaces standard HTML <select> for searchable dropdowns.
 * 
 * @param {Object[]} options - Array of objects with { value, label }
 * @param {string} value - Selected value
 * @param {function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the combobox is disabled
 * @param {string} className - Additional classes
 */
const Combobox = ({
  options = [],
  value,
  onChange,
  placeholder = "Select option...",
  disabled = false,
  className,
  error,
  label,
  icon: Icon,
  id
}) => {
  const [open, setOpen] = useState(false);
  const selectionGuardRef = useRef(false);

  useEffect(() => {
    if (open) selectionGuardRef.current = false;
  }, [open]);

  const selectWithMouse = (e, optionValue) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectionGuardRef.current) return;
    selectionGuardRef.current = true;
    onChange(optionValue);
    setOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={cn("relative w-full", className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            {Icon && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                <Icon className="h-5 w-5" />
              </div>
            )}
            <button
              type="button"
              id={id}
              onClick={() => {
                if (!disabled) setOpen(!open);
              }}
              className={cn(
                "flex min-h-10 h-auto w-full items-center justify-between rounded-md border px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                Icon && "pl-10",
                error ? "border-red-500" : "border-slate-200",
                disabled && "bg-slate-100 text-slate-500",
                className
              )}
              disabled={disabled}
              role="combobox"
              aria-expanded={open}
            >
              <span>
                {selectedOption ? selectedOption.label : (value || placeholder)}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[90vw] p-0" align="start">
          <Command className="min-w-full h-auto">
            <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label} // Search by label
                    onSelect={() => {
                      if (selectionGuardRef.current) return;
                      selectionGuardRef.current = true;
                      onChange(option.value);
                      setOpen(false);
                    }}
                    onMouseDown={(e) => selectWithMouse(e, option.value)}
                    onClick={(e) => selectWithMouse(e, option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="break-words text-left">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default Combobox;
